import pointerHelper, { hoverHelper } from '@Common/Helpers/PointerHelper';
import * as commonUtils from '@Common/Helpers/CommonUtils';

import { IControlParameters } from '@Common/Controls/BaseControl';
import { BaseMenu } from '@Common/Controls/BaseMenu';
import { ExecuteAction, MenuItemType } from '@Common/Enums';

export interface IMenuParameters extends IControlParameters {
    levelStyle?: any[];
    contentStyle?: string;
    sortable?: boolean;
    selectable?: boolean;
    isVertical?: boolean;
    itemSpacingType?: string;
    itemSpacing?: number;
}

export class Menu extends BaseMenu {
    constructor(param?: IMenuParameters) {
        super(
            $.extend(
                {
                    isVertical: false,
                    sortable: false
                },
                param
            )
        );
    }

    protected _init() {
        super._init();
        this._root.addClass('menu-control');
    }

    public addChildrens(node) {
        node.control.childrenUl = $('<ol class="menu-children vertical"/>')
            .addClass(this.param.contentStyle)
            .addClass(this.param.defaultContentStyle);

        !this.param.isDesktop && node.control.childrenUl.addClass('mobile');

        _(node.children).forEach(
            function (i) {
                this._createItem(i);
                i.control.li && i.control.li.appendTo(node.control.childrenUl);
            }.bind(this)
        );

        node.control.childrenUl.on(
            'removePopup',
            function () {
                node.control.childrenUl.detach();
                node.control.childrenUlOpened = false;
                this._itemHoverOut(node);
            }.bind(this)
        );
    }

    private _detachChildrenByLevel(nodes, node) {
        _(nodes).forEach(
            function (i) {
                if (i.param.level >= node.param.level && i.control && i.control.childrenUl) {
                    i.control.childrenUl.trigger('removePopup');
                }

                this._detachChildrenByLevel(i.children || [], node);
            }.bind(this)
        );
    }

    private _itemHoverOn(node) {
        function detachChildren(items) {
            _(items).forEach(function (i) {
                if (i.control && i.control.childrenUl) {
                    i.control.childrenUl.trigger('removePopup').detach();
                }
                detachChildren(i.children || []);
            });
        }

        if (!node.param.level) {
            detachChildren(this._treeStore.nodes());
        } else {
            this._detachChildrenByLevel(this._treeStore.nodes(), node);
        }

        node.control && node.control.setHoverStyle();

        if (node.hasChildren) {
            node.control.childrenUl.appendTo(this._body);
            node.control.childrenUlOpened = true;
        }
        this._setChildrenCoordinate(node);
    }

    private _itemHoverOut(node) {
        if (node.hasChildren && node.control.childrenUl.elementInDOM()) {
            node.control.setOpenedStyle();
        } else {
            const isSelected = node.data.selected || node.control.selected;

            if (isSelected || node.control.liContent.hasClass('selected')) {
                node.control.setSelectedStyle();
            } else {
                node.control.setDefaultStyle();
            }
        }
        node.control.childrenUlOpened = false;
    }

    public itemHover(node) {
        node.control.liContent
            .on(
                pointerHelper.pointerOverEventName,
                function (e) {
                    if (hoverHelper.isEnter(e)) {
                        this._itemHoverOn(node);
                    }
                }.bind(this)
            )
            .on(
                pointerHelper.pointerOutEventName,
                function (e) {
                    if (hoverHelper.isLeave(e)) {
                        this._itemHoverOut(node);
                    }
                }.bind(this)
            );
    }

    protected _clickHandler(node, leftToRight, event) {
        super._clickHandler(node, leftToRight, event);

        function hideULChildren(items) {
            _(items).forEach(function (i) {
                i.control && i.control.childrenUl && i.control.childrenUl.trigger('removePopup');
                hideULChildren(i.nodes() || []);
            });
        }

        hideULChildren(this._treeStore.nodes());
    }

    public bindLicontentEvent(node) {
        const item = node.data;
        const isDynamic = item.menuItemType == MenuItemType.dynamicItem;
        const hasAction = item.executeAction != ExecuteAction.none && !isDynamic;
        const isFolder = item.isFolder || isDynamic;
        const canHaveChildren = node.hasChildren || item.dynamic === true;

        if (item.disable) {
            return;
        }

        let arrow;
        const onOpenChildren = function () {
            if (canHaveChildren) {
                if (node.control.childrenUlOpened) {
                    this._itemHoverOut(node);
                    node.control.childrenUlOpened = false;
                    node.control.li.removeClass('opened');
                    this._detachChildrenByLevel(this._treeStore.nodes(), node);
                } else {
                    this._itemHoverOn(node);
                    node.control.childrenUlOpened = true;
                    node.control.li.addClass('opened');
                }
            }
        }.bind(this);

        if (node.hasChildren && this.param.showContainerIndicator) {
            arrow = $('<span class="arrow"/>').appendTo(node.control.liContent);
        }

        // click by li content
        node.control.liContent
            .on(pointerHelper.pointerDownEventName, function (e) {
                e.preventClearPopups();
            })
            .click(
                function (e) {
                    const openChildren = hasAction
                        ? arrow
                            ? e.target == arrow[0]
                            : !node.control.childrenUlOpened
                        : item.isFolder || !node.control.childrenUlOpened;

                    if (node.hasChildren && openChildren) {
                        onOpenChildren();
                    } else {
                        this._clickHandler(node, null, e);
                        commonUtils.clearPopups();
                    }
                }.bind(this)
            );

        // need we check box in menu ???
        // click by check box
        if (this.param.checkable) {
            node.control.checkbox.click(
                function (e) {
                    node.check(node.checked === true ? false : true, { clientCheck: true });
                }.bind(this)
            );

            if (this.param.checkOnClick) {
                node.control.liContent.click(function (e) {
                    node.check(node.checked === true ? false : true, { clientCheck: true });
                });
            }
        }

        // double click
        if (this.param.isDesktop) {
            node.control.liContent.doubleClick(
                function () {
                    this._onItemDoubleClick(item);
                }.bind(this)
            );
        }

        // mobile icon
        if (canHaveChildren) {
            node.control.showArrow();
        }
    }

    protected _setChildrenCoordinate(node) {
        const childrenUl = node.control.childrenUl;
        const li = node.control.li;

        if (childrenUl) {
            let left, top;
            const childMargin = 1;
            const isVertical = li.parent().hasClass('vertical');

            // calculate left offset
            const ulWidth = childrenUl.outerWidth(true);
            const windowWidth = $(window).outerWidth();

            if (isVertical) {
                left = li.offset().left + li.outerWidth(true);

                if (windowWidth - left - ulWidth < 0) {
                    left = li.offset().left - ulWidth - childMargin;
                }
            } else {
                left = li.offset().left;

                if (windowWidth - left - ulWidth < 0) {
                    left = windowWidth - ulWidth - childMargin;
                }
            }

            // calculate top offset
            const ulHeight = childrenUl.outerHeight(true);
            const liOffsetTop = li.offset().top;

            if (isVertical) {
                const possibleHeight = $(window).outerHeight() - liOffsetTop;

                if (possibleHeight < ulHeight) {
                    top = liOffsetTop - (ulHeight - possibleHeight);
                    -1 * top > liOffsetTop && (top = -1 * liOffsetTop);
                } else {
                    top = liOffsetTop;
                }
            } else {
                const possibleBottomHeight = $(window).outerHeight(true) - this._root.offset().top - this._root.outerHeight(true);
                const possibleTopHeight = this._root.offset().top;

                if (possibleBottomHeight > ulHeight) {
                    // show on bottom
                    top = li.offset().top + li.outerHeight(true);
                } else if (possibleBottomHeight < ulHeight && possibleTopHeight > ulHeight) {
                    // show on top
                    top = this._root.offset().top - ulHeight;
                } else {
                    // show on bottom
                    top = 0;
                }
            }

            childrenUl.css({
                top: top,
                left: left
            });
        }
    }
}
